/**
 * useRiderData Hook
 * 라이더 정산 목록 조회 및 출금제한 관리 훅
 */
import { useCallback, useEffect, useState } from 'react';
import { riderAccountingApi } from '../api/riderAccountingApi';
import { partnerAccountingApi } from '../api/partnerAccountingApi';
import { ContractStatusOption, RiderListItem } from '../types/riderAccounting.types';
import { PartnerNameItem } from '../types/partnerAccounting.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { getItem } from '@/shared/utils/storage';

export const useRiderData = () => {
    const { showToast } = useToast();

    const userType = getItem<string>('USER_TYPE');
    const userId = getItem<string>('USER_ID');

    const [searchRiderKeyword, setSearchRiderKeyword] = useState('');
    const [searchContractStatus, setSearchContractStatus] = useState('');
    const [searchPartnerId, setSearchPartnerId] = useState('');
    const [searchWithdrawalStatus, setSearchWithdrawalStatus] = useState('');
    const [allData, setAllData] = useState<RiderListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [contractStatusCodes, setContractStatusCodes] = useState<ContractStatusOption[]>([]);
    const [contractStatusLoading, setContractStatusLoading] = useState(false);
    const [partnerNames, setPartnerNames] = useState<PartnerNameItem[]>([]);

    const fetchRiderList = useCallback(async () => {
        try {
            setLoading(true);

            let response;
            if (userType === 'SUPPLIER' && userId) {
                response = await riderAccountingApi.searchRiders({ suppId: userId });
            } else {
                response = await riderAccountingApi.getRiderList();
            }

            setAllData(Array.isArray(response) ? response : []);
        } catch {
            showToast('라이더 목록 조회에 실패했습니다.', { type: ToastType.ERROR });
            setAllData([]);
        } finally {
            setLoading(false);
        }
    }, [userType, userId, showToast]);

    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);

            const partnerId = userType === 'SUPPLIER' && userId ? userId : searchPartnerId;

            const params: Record<string, string> = {};
            if (partnerId) params.suppId = partnerId;
            if (searchRiderKeyword) {
                const isName = /[가-힣]/.test(searchRiderKeyword);
                if (isName) {
                    params.riderName = searchRiderKeyword;
                } else {
                    params.riderId = searchRiderKeyword;
                }
            }
            if (searchContractStatus) params.contStatus = searchContractStatus;
            if (searchWithdrawalStatus) params.withdrawalYn = searchWithdrawalStatus;

            const response = await riderAccountingApi.searchRiders(params);
            setAllData(Array.isArray(response) ? response : []);
        } catch {
            showToast('라이더 검색에 실패했습니다.', { type: ToastType.ERROR });
            setAllData([]);
        } finally {
            setLoading(false);
        }
    }, [userType, userId, searchPartnerId, searchRiderKeyword, searchContractStatus, searchWithdrawalStatus, showToast]);

    const executeWithdrawalLimit = useCallback(async (
        riderId: string,
        suppId: string,
        newStatus: 'Y' | 'N',
        riderName: string,
        action: string
    ) => {
        try {
            setLoading(true);
            await riderAccountingApi.updateWithdrawalStatus(riderId, suppId, newStatus);
            showToast(`${riderName} ${action}이 적용되었습니다.`, { type: ToastType.SUCCESS });
            await fetchRiderList();
        } catch {
            showToast(`${action} 설정에 실패했습니다.`, { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchRiderList]);

    const fetchContractStatusCodes = useCallback(async () => {
        try {
            setContractStatusLoading(true);
            const response = await riderAccountingApi.getContractStatusCodes();
            setContractStatusCodes(response || []);
        } catch {
            setContractStatusCodes([]);
        } finally {
            setContractStatusLoading(false);
        }
    }, []);

    const resetSearchConditions = useCallback(() => {
        setSearchRiderKeyword('');
        setSearchContractStatus('');
        setSearchPartnerId('');
        setSearchWithdrawalStatus('');
    }, []);

    useEffect(() => {
        const initialize = async () => {
            resetSearchConditions();
            fetchContractStatusCodes();

            if (userType === 'HEADQUARTERS') {
                try {
                    setLoading(true);
                    const names = await partnerAccountingApi.getPartnerNames();
                    setPartnerNames(names || []);

                    if (names && names.length > 0) {
                        const firstSupplier = names[0];
                        setSearchPartnerId(firstSupplier.suppId);

                        const response = await riderAccountingApi.searchRiders({ suppId: firstSupplier.suppId });
                        setAllData(Array.isArray(response) ? response : []);
                    }
                } catch {
                    showToast('데이터 조회에 실패했습니다.', { type: ToastType.ERROR });
                    setAllData([]);
                } finally {
                    setLoading(false);
                }
            } else {
                fetchRiderList();
            }
        };

        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        searchRiderKeyword,
        setSearchRiderKeyword,
        searchContractStatus,
        setSearchContractStatus,
        searchPartnerId,
        setSearchPartnerId,
        searchWithdrawalStatus,
        setSearchWithdrawalStatus,
        allData,
        loading,
        userType,
        contractStatusCodes,
        contractStatusLoading,
        partnerNames,
        handleSearch,
        executeWithdrawalLimit,
        fetchRiderList,
        resetSearchConditions,
    };
};
